import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react";
import { Card, Input, Button, message as antMessage, Avatar, Empty } from "antd";
import { SendOutlined, UserOutlined } from "@ant-design/icons";
import { BASE_API_CONFIG, axiosInstance } from "../../util/Api";
import "../messages/messages.less";
import { useDispatch, useSelector } from "react-redux";
import { getMessagesAction } from "../../appRedux/actions/requestQuotationAction";
import { formatDate, formatTime } from "../../util/helper";

const { TextArea } = Input;

const Messages = forwardRef(({ id, loggedInUserId, isQuotation, quotationDetails, receiverId: propReceiverId, receiverName: propReceiverName }, ref) => {
  const dispatch = useDispatch();
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [showChatInput, setShowChatInput] = useState(false);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const messagesEndRef = useRef(null);
  const chatInputRef = useRef(null);
  const chatContainerRef = useRef(null); // Add this ref for the container
  
  const messageData = useSelector(
    (state) => state.requestQuotations.QuotationMessages
  );

  const hasMessages = messageData && messageData.length > 0;

  // Expose methods to parent component using useImperativeHandle
  useImperativeHandle(ref, () => ({
    focusChatInput: () => {
      // Force show chat input if there are no messages
      if (!hasMessages && !showChatInput) {
        setShowChatInput(true);
      }
      
      // Use setTimeout to ensure DOM is updated before focusing
      setTimeout(() => {
        if (chatInputRef.current) {
          chatInputRef.current.focus();
          // Scroll the container div into view instead of the textarea
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        } else {
          // If chat input ref is not available yet, try again after a short delay
          setTimeout(() => {
            if (chatInputRef.current) {
              chatInputRef.current.focus();
              if (chatContainerRef.current) {
                chatContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }
          }, 100);
        }
      }, 100);
    },
    scrollIntoView: (options) => {
      // For scrolling the entire messages component into view
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollIntoView(options);
      }
    }
  }));

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    // Validate IDs before fetching
    if (!id) {
      console.error('Messages component: id is undefined or null', { 
        id, 
        isQuotation, 
        loggedInUserId,
        quotationDetails 
      });
      return;
    }

    if (!loggedInUserId) {
      console.error('Messages component: loggedInUserId is undefined or null');
      return;
    }

    // Only fetch if we have valid IDs and haven't fetched yet
    if (id && loggedInUserId && !initialFetchDone) {
      console.log('Fetching messages with params:', { 
        type: isQuotation ? 'quotation' : 'enquiry',
        id: id, 
        userId: loggedInUserId,
        idType: typeof id,
        userIdType: typeof loggedInUserId
      });
      
      // Ensure id is a number if it should be
      const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
      const numericUserId = typeof loggedInUserId === 'string' ? parseInt(loggedInUserId, 10) : loggedInUserId;
      
      if (isNaN(numericId) || isNaN(numericUserId)) {
        console.error('Invalid numeric IDs:', { numericId, numericUserId });
        return;
      }
      
      dispatch(getMessagesAction(isQuotation, numericId, numericUserId));
      setInitialFetchDone(true);
    }
  }, [id, loggedInUserId, isQuotation, dispatch, initialFetchDone, quotationDetails]);

  useEffect(() => {
    if (hasMessages) {
      scrollToBottom();
    }
  }, [messageData]);

  const handleSendMessage = async () => {
    // Validate required fields before sending
    if (!id) {
      antMessage.error("Cannot send message: Missing conversation ID");
      return;
    }

    if (!loggedInUserId) {
      antMessage.error("Cannot send message: User not logged in");
      return;
    }

    if (!replyText.trim()) {
      antMessage.warning("Please enter a message");
      return;
    }

    setSending(true);
    try {
      let receiverId, receiverName;
      
      // STRATEGY 1: When replying to a specific message
      if (replyingTo) {
        receiverId = replyingTo.senderId === loggedInUserId 
          ? replyingTo.receiverId 
          : replyingTo.senderId;
        receiverName = replyingTo.sender?.companyName || replyingTo.sender || 'User';
      } 
      // STRATEGY 2: Get from existing messages
      else if (messageData && messageData.length > 0) {
        const otherMessage = messageData.find(m => m.senderId !== loggedInUserId);
        if (otherMessage) {
          receiverId = otherMessage.senderId;
          receiverName = otherMessage.sender?.companyName || otherMessage.sender || 'User';
        }
      }
      // STRATEGY 3: Get from quotationDetails (for first message)
      else if (quotationDetails) {
        console.log('Determining receiver from quotationDetails:', quotationDetails);
        
        // Check different possible structures of quotationDetails
        if (quotationDetails.senderId && quotationDetails.receiverId) {
          // Direct sender/receiver fields
          if (quotationDetails.senderId === loggedInUserId) {
            receiverId = quotationDetails.receiverId;
            receiverName = quotationDetails.receiver?.companyName || 
                          quotationDetails.receiverName || 
                          'Receiver';
          } else if (quotationDetails.receiverId === loggedInUserId) {
            receiverId = quotationDetails.senderId;
            receiverName = quotationDetails.sender?.companyName || 
                          quotationDetails.senderName || 
                          'Sender';
          }
        } 
        // Alternative structure: createdBy and assignedTo
        else if (quotationDetails.createdBy && quotationDetails.assignedTo) {
          if (quotationDetails.createdBy.id === loggedInUserId) {
            receiverId = quotationDetails.assignedTo.id;
            receiverName = quotationDetails.assignedTo.companyName || 'Receiver';
          } else if (quotationDetails.assignedTo.id === loggedInUserId) {
            receiverId = quotationDetails.createdBy.id;
            receiverName = quotationDetails.createdBy.companyName || 'Sender';
          }
        }
        // Alternative structure: user and otherUser
        else if (quotationDetails.userId && quotationDetails.otherUserId) {
          if (quotationDetails.userId === loggedInUserId) {
            receiverId = quotationDetails.otherUserId;
            receiverName = quotationDetails.otherUserName || 'User';
          } else if (quotationDetails.otherUserId === loggedInUserId) {
            receiverId = quotationDetails.userId;
            receiverName = quotationDetails.userName || 'User';
          }
        }
        // Check if quotationDetails has a participants array
        else if (quotationDetails.participants && Array.isArray(quotationDetails.participants)) {
          const otherParticipant = quotationDetails.participants.find(p => p.id !== loggedInUserId);
          if (otherParticipant) {
            receiverId = otherParticipant.id;
            receiverName = otherParticipant.companyName || otherParticipant.name || 'User';
          }
        }
      }
      
      // STRATEGY 4: If we have the other user's ID from props
      if (!receiverId && propReceiverId) {
        receiverId = propReceiverId;
        receiverName = propReceiverName || 'User';
      }
      
      // STRATEGY 5: If we have the user ID from URL or context (passed as prop)
      if (!receiverId && quotationDetails?.userId) {
        // If logged in user is the main user, receiver might be the business user
        if (quotationDetails.userId === loggedInUserId && quotationDetails.businessUserId) {
          receiverId = quotationDetails.businessUserId;
          receiverName = quotationDetails.businessUserName || 'Business User';
        }
      }
      
      // STRATEGY 6: Last resort - check if this is an RFQ (Request for Quotation)
      if (!receiverId && isQuotation) {
        // For RFQ, the receiver is typically the business owner or admin
        // You might need to pass the business owner ID as a prop
        if (quotationDetails?.businessOwnerId) {
          receiverId = quotationDetails.businessOwnerId;
          receiverName = quotationDetails.businessOwnerName || 'Business Owner';
        }
      }
      
      // If still no receiver, show error with debug info
      if (!receiverId) {
        console.error('Could not determine receiver. Available data:', {
          id,
          isQuotation,
          loggedInUserId,
          quotationDetails,
          messageData: messageData ? messageData.length : 0,
          replyingTo
        });
        
        antMessage.error({
          content: "Cannot determine message recipient. Please refresh and try again.",
          duration: 5
        });
        setSending(false);
        return;
      }

      // Ensure IDs are numbers
      const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
      const numericSenderId = typeof loggedInUserId === 'string' ? parseInt(loggedInUserId, 10) : loggedInUserId;
      const numericReceiverId = typeof receiverId === 'string' ? parseInt(receiverId, 10) : receiverId;

      if (isNaN(numericId) || isNaN(numericSenderId) || isNaN(numericReceiverId)) {
        console.error('Invalid numeric IDs:', { numericId, numericSenderId, numericReceiverId });
        antMessage.error("Invalid IDs found. Please refresh the page.");
        setSending(false);
        return;
      }

      // Prepare message payload
      const messagePayload = {
        text: replyText,
        senderId: numericSenderId,
        sender: "You",
        receiverId: numericReceiverId,
        receiver: receiverName || 'User',
        parentMessageId: replyingTo?.id || null
      };

      // Add the relevant ID based on isQuotation flag
      if (isQuotation) {
        messagePayload.quotationId = numericId;
      } else {
        messagePayload.enquiryId = numericId;
      }

      console.log('Final message payload:', messagePayload);

      const response = await axiosInstance.post(
        `${BASE_API_CONFIG.baseURL}/messages`, 
        messagePayload
      );

      if (response.data) {
        antMessage.success(replyingTo ? "Reply sent successfully" : "Message sent successfully");
        setReplyText("");
        setReplyingTo(null);
        setShowChatInput(false);
        
        // Refresh messages after sending
        if (numericId && numericSenderId) {
          dispatch(getMessagesAction(isQuotation, numericId, numericSenderId));
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
      antMessage.error(error.response?.data?.message || "Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const MessageBubble = ({ message, isOwn }) => (
    <div className={`message-bubble-wrapper ${isOwn ? 'own' : 'other'}`}>
      <div className="message-avatar">
        <Avatar 
          icon={<UserOutlined />} 
          size={40}
          style={{ 
            backgroundColor: isOwn ? '#0E7AFE' : '#f56a00',
          }}
        />
      </div>
      <div className="message-content">
        <div className="message-sender">
          {message?.sender?.companyName || message?.sender || 'User'}
        </div>
        <div className={`message-bubble ${isOwn ? 'own' : 'other'}`}>
          <div className="message-text">{message.text}</div>
          <div className="message-time">
            {formatTime(message.createdAt)}
          </div>
        </div>
        {!isOwn && (
          <Button
            type="link"
            size="small"
            onClick={() => setReplyingTo(message)}
            className="message-reply-btn"
          >
            Reply
          </Button>
        )}
      </div>
    </div>
  );

  const ReplyBubble = ({ message, parentMessage }) => (
    <div className="reply-bubble-wrapper">
      <div className="reply-indicator">
        <div className="reply-line"></div>
        <div className="reply-preview">
          Replying to {parentMessage?.sender?.companyName || parentMessage?.sender || 'User'}
        </div>
      </div>
      <MessageBubble message={message} isOwn={message.senderId === loggedInUserId} />
    </div>
  );

  const renderMessages = () => {
    if (!hasMessages) {
      return null;
    }
    console.log('Sample message date:', messageData[0]?.createdAt);

    // Group messages by date
    const groupedMessages = messageData.reduce((groups, message) => {
      const date = formatDate(message.createdAt);
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
      return groups;
    }, {});

    return Object.entries(groupedMessages).map(([date, messages]) => (
      <div key={date} className="message-group">
        <div className="date-divider">
          <span className="date-text">{date}</span>
        </div>
        {messages.map((message) => {
          const isOwn = message.senderId === loggedInUserId;
          
          if (message.parentMessageId) {
            const parentMessage = messageData.find(m => m.id === message.parentMessageId);
            return (
              <ReplyBubble 
                key={message.id} 
                message={message} 
                parentMessage={parentMessage}
              />
            );
          }
          
          return <MessageBubble key={message.id} message={message} isOwn={isOwn} />;
        })}
      </div>
    ));
  };

  // Show debug info if IDs are missing
  if (!id) {
    console.warn('Messages component: id is missing', { id, isQuotation, loggedInUserId });
    return (
      <Card className="myexim-detail-card chat-card">
        <div className="chat-header">
          <div className="chat-title">Messages</div>
        </div>
        <div className="chat-empty-state">
          <Empty 
            description="Unable to load messages: Conversation ID is missing" 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      </Card>
    );
  }

  if (!loggedInUserId) {
    return (
      <Card className="myexim-detail-card chat-card">
        <div className="chat-header">
          <div className="chat-title">Messages</div>
        </div>
        <div className="chat-empty-state">
          <Empty 
            description="Please log in to view messages" 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      </Card>
    );
  }

  // If no messages and not showing input, show placeholder
  if (!hasMessages && !showChatInput) {
    return (
      <Card className="myexim-detail-card chat-card chat-card-empty" ref={chatContainerRef}>
        <div className="chat-header">
          <div className="chat-title">Messages</div>
        </div>
        
        <div className="chat-empty-state">
          <Empty 
            description="No conversation started yet" 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
          <div className="chat-empty-footer">
            <Button 
              type="primary"
              onClick={() => setShowChatInput(true)}
            >
              Start Conversation
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // Show chat interface
  return (
    <Card className="myexim-detail-card chat-card" ref={chatContainerRef}>
      <div className="chat-header">
        <div className="chat-title">Messages</div>
      </div>
      
      {hasMessages && (
        <div className="chat-messages-container">
          {renderMessages()}
          <div ref={messagesEndRef} />
        </div>
      )}

      {!hasMessages && showChatInput && (
        <div className="chat-empty-state">
          <Empty 
            description="Start a new conversation" 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      )}

      {replyingTo && (
        <div className="replying-bar">
          <span className="replying-text">
            Replying to {replyingTo.sender?.companyName || replyingTo.sender || 'User'}
          </span>
          <Button 
            type="text" 
            size="small" 
            onClick={() => {
              setReplyingTo(null);
              setReplyText("");
            }}
            className="cancel-reply-btn"
          >
            Cancel
          </Button>
        </div>
      )}

      <div className="chat-input-container">
        <TextArea
          ref={chatInputRef}
          rows={2}
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={replyingTo ? "Type your reply..." : "Type your message..."}
          className="chat-input"
          autoFocus={showChatInput}
        />
        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={handleSendMessage}
          loading={sending}
          className="send-button"
          disabled={!replyText.trim()}
        >
          Send
        </Button>
      </div>
    </Card>
  );
});

export default Messages;